import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "components/ui/input";
import { Search, CheckCircle2, AlertCircle } from "lucide-react";
import { getLhcCns, linkCnToLhc, CnLhcDetail } from "api/lhcUpdate";
import { useBreadcrumbs } from "contexts/BreadcrumbsContext";
import { Alert, AlertDescription } from "components/ui/alert";
import { LhcCnsTable } from "./components/LhcCnsTable";
import { LinkCnDialog } from "./components/LinkCnDialogs";

const LhcUpdatePage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchedLhc, setSearchedLhc] = useState<string>("");
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [cnLhcList, setCnLhcList] = useState<CnLhcDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Link Modal States
  const [linkModalOpen, setLinkModalOpen] = useState<boolean>(false);
  const [cnInput, setCnInput] = useState<string>("");
  const [cnInputError, setCnInputError] = useState<string>("");
  const [linking, setLinking] = useState<boolean>(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Settings", href: "/settings" },
      { label: "LHC Update" },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const lhcFromUrl = searchParams.get("lhcNumber");
    if (lhcFromUrl) {
      setSearchQuery(lhcFromUrl);
      fetchLhc(lhcFromUrl);
    }
  }, []);

  const fetchLhc = async (lhcNumber: string) => {
    const trimmed = lhcNumber.trim();
    if (!trimmed) return;

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setHasSearched(true);
      setSearchedLhc(trimmed);

      const res = await getLhcCns(trimmed);
      setCnLhcList(res.cn_lhcs || []);
    } catch (err: any) {
      console.error("Failed to fetch LHC CNs", err);
      setCnLhcList([]);
      setError(err?.data?.detail || err?.response?.data?.detail || err?.message || "Failed to fetch linked CN numbers");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchParams({ lhcNumber: searchQuery.trim() });
    fetchLhc(searchQuery.trim());
  };

  const handleOpenLinkModal = () => {
    setCnInput("");
    setCnInputError("");
    setLinkError(null);
    setLinkModalOpen(true);
  };

  const handleSubmitLink = async () => {
    const trimmedCn = cnInput.trim();
    if (!trimmedCn) {
      setCnInputError("Please enter a CN number");
      return;
    }
    setCnInputError("");
    setLinkError(null);

    if (!searchedLhc) return;

    try {
      setLinking(true);
      const res = await linkCnToLhc(searchedLhc, trimmedCn);

      setSuccessMessage(res.message || `Successfully linked CN ${trimmedCn} to LHC ${searchedLhc}`);
      setLinkModalOpen(false);
      setCnInput("");

      fetchLhc(searchedLhc);
    } catch (err: any) {
      console.error("Link error", err);
      const detail = err?.data?.detail || err?.response?.data?.detail || err?.message || "Failed to link CN number";
      setLinkError(detail);
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="px-6 py-2 space-y-6">
      <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
        <div className="relative w-fit">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search LHC"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 w-[220px] pl-8 text-xs font-normal text-gray-600 focus-visible:ring-gray-400"
          />
        </div>

        <button type="submit" className="h-8 px-3 text-xs font-medium rounded-md bg-black text-white hover:bg-gray-800 transition-colors">Search</button>
      </form>

      {successMessage && (
        <div className="mx-1">
          <Alert className="bg-emerald-50 border-emerald-200 text-emerald-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <AlertDescription className="text-xs font-medium">{successMessage}</AlertDescription>
            </div>
            <button onClick={() => setSuccessMessage(null)} className="text-xs text-emerald-700 hover:text-emerald-900 font-bold ml-4">Dismiss</button>
          </Alert>
        </div>
      )}

      {error && (
        <div className="mx-1">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs font-medium">{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading...</p>}

      {!loading && !hasSearched && (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
          <img src="/images/truck.png" alt="Truck" className="w-28 opacity-70 mb-6" />
          <h3 className="text-lg font-semibold text-gray-800">Search for an LHC Number</h3>
          <p className="text-sm text-gray-500 mt-1 max-w-sm">Enter an LHC number above to view linked CN numbers and link new ones.</p>
        </div>
      )}

      {/* Results View - Table Component */}
      {!loading && hasSearched && !error && (
        <LhcCnsTable
          searchedLhc={searchedLhc}
          cnLhcList={cnLhcList}
          onOpenLinkModal={handleOpenLinkModal}
        />
      )}

      {/* Direct Link Modal Component */}
      <LinkCnDialog
        searchedLhc={searchedLhc}
        linkModalOpen={linkModalOpen}
        setLinkModalOpen={setLinkModalOpen}
        cnInput={cnInput}
        setCnInput={setCnInput}
        cnInputError={cnInputError}
        setCnInputError={setCnInputError}
        linking={linking}
        linkError={linkError}
        onSubmitLink={handleSubmitLink}
      />
    </div>
  );
};

export default LhcUpdatePage;
